import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  explainWhyVenmoUsernameNotParsed,
  parseVenmoUsernameFromUrl,
  validateVenmoUsernameCapitalization,
} from "../utils/venmo";
import { openVenmoProfile } from "../services/Venmo";

/**
 * Reusable Venmo username editor section (used by Create/Edit participant modals).
 * Includes:
 * - Optional toggle wrapper
 * - Username / Link tabs
 * - Link parsing + error messaging
 * - "How to find ..." instructions (mock + translated text)
 */
export default function VenmoUsernameSection({
  idBase = "venmo",
  modalId,
  enabled,
  onEnabledChange,
  venmoUsername,
  setVenmoUsername,
  resetSignal,
  onValidationChange,
}) {
  const { t } = useTranslation();

  const [venmoTab, setVenmoTab] = useState("username"); // 'username' | 'link'
  const [showVenmoHelp, setShowVenmoHelp] = useState(false);
  const [showVenmoLinkHint, setShowVenmoLinkHint] = useState(false);
  const [venmoProfileLink, setVenmoProfileLink] = useState("");
  const [venmoError, setVenmoError] = useState("");

  const normalizedUsername = useMemo(() => {
    return String(venmoUsername || "").trim().replace(/^@/, "");
  }, [venmoUsername]);

  // Align Bootstrap's built-in :valid / :invalid styling with our capitalization rule.
  // - Optional leading "@"
  // - One or more name parts separated by "-"
  // - Each name part starts with a capital letter
  // - Optional numeric suffix like "-11"
  const venmoUsernamePattern = useMemo(
    () => "^@?[A-Z][A-Za-z]*(-[A-Z][A-Za-z]*)*(-\\d+)?$",
    []
  );

  const isVenmoOk = useMemo(() => {
    if (!enabled) return false;
    if (venmoError) return false;
    if (!normalizedUsername) return false;
    // If user is on link tab, require that they actually provided a link to parse.
    if (venmoTab === "link") return !!String(venmoProfileLink || "").trim();
    return true;
  }, [enabled, normalizedUsername, venmoError, venmoProfileLink, venmoTab]);

  const toggleId = useMemo(() => `${idBase}-toggle`, [idBase]);

  const resetVenmoUi = useCallback(() => {
    setVenmoError("");
    setShowVenmoHelp(false);
    setShowVenmoLinkHint(false);
    setVenmoTab("username");
    setVenmoProfileLink("");
  }, []);

  useEffect(() => {
    if (resetSignal === undefined) return;
    resetVenmoUi();
  }, [resetSignal, resetVenmoUi]);

  const getVenmoUsernameError = useCallback(
    (username) => {
      const code = validateVenmoUsernameCapitalization(username);
      if (!code) return null;
      // Currently only one rule; keep it extensible via codes.
      return (
        t("participant.venmo_capitalization_error") ||
        "Each name part must start with a capital letter (e.g., Tom-Cat-11)"
      );
    },
    [t]
  );

  // Validate venmo username whenever it changes or when enabled state changes
  useEffect(() => {
    if (!enabled) {
      // When disabled, validation is always ok
      if (typeof onValidationChange === "function") {
        onValidationChange(true);
      }
      return;
    }
    
    // When enabled, validate the username
    const error = getVenmoUsernameError(venmoUsername);
    if (typeof onValidationChange === "function") {
      onValidationChange(!error);
    }
  }, [enabled, venmoUsername, getVenmoUsernameError, onValidationChange]);

  const handleEnableChange = useCallback(
    (next) => {
      if (typeof onEnabledChange === "function") onEnabledChange(next);
      if (!next) {
        resetVenmoUi();
        setVenmoUsername("");
        // When disabling, validation is always ok
        if (typeof onValidationChange === "function") {
          onValidationChange(true);
        }
      } else {
        // When enabling, validate the current username
        const error = getVenmoUsernameError(venmoUsername);
        if (typeof onValidationChange === "function") {
          onValidationChange(!error);
        }
      }
    },
    [onEnabledChange, resetVenmoUi, setVenmoUsername, onValidationChange, getVenmoUsernameError, venmoUsername]
  );

  const handleVenmoLinkChange = useCallback(
    (value) => {
      setVenmoProfileLink(value);
      setVenmoError("");

      if (!value.trim()) {
        setVenmoUsername("");
        if (typeof onValidationChange === "function") {
          onValidationChange(true);
        }
        return;
      }

      const username = parseVenmoUsernameFromUrl(value);
      if (!username) {
        const why = explainWhyVenmoUsernameNotParsed(value);
        setVenmoUsername("");
        setVenmoError(
          why === "venmo_code_user_id"
            ? t("participant.venmo_parse_failed_user_id")
            : t("participant.venmo_parse_failed")
        );
        if (typeof onValidationChange === "function") {
          onValidationChange(false);
        }
        return;
      }

      // Validate the extracted username
      const validationError = getVenmoUsernameError(username);
      if (validationError) {
        setVenmoError(validationError);
        setVenmoUsername("");
        if (typeof onValidationChange === "function") {
          onValidationChange(false);
        }
        return;
      }

      setVenmoUsername(username);
      if (typeof onValidationChange === "function") {
        onValidationChange(true);
      }
    },
    [setVenmoUsername, t, getVenmoUsernameError, onValidationChange]
  );

  const handleVenmoUsernameChange = useCallback(
    (value) => {
      setVenmoUsername(value);
      const error = getVenmoUsernameError(value);
      setVenmoError(error || "");
      if (typeof onValidationChange === "function") {
        onValidationChange(!error);
      }
    },
    [setVenmoUsername, getVenmoUsernameError, onValidationChange]
  );

  const handleGoToVenmo = useCallback(() => {
    openVenmoProfile({ username: venmoUsername, sourceModalId: modalId });
  }, [venmoUsername, modalId]);

  const VenmoHelpMock = ({ highlight = "copy" } = {}) => {
    const shell = {
      background: "#ffffff",
      border: "1px solid rgba(15, 23, 42, 0.14)",
      borderRadius: 16,
      padding: 12,
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
    };

    // iOS-like segmented control
    const seg = {
      display: "flex",
      gap: 6,
      background: "#eef2f7",
      border: "1px solid rgba(15, 23, 42, 0.12)",
      borderRadius: 999,
      padding: 5,
      fontSize: 12,
      color: "#0f172a",
    };

    const segItem = (active) => ({
      flex: 1,
      textAlign: "center",
      padding: "7px 8px",
      borderRadius: 999,
      background: active ? "#ffffff" : "transparent",
      border: active ? "1px solid rgba(15, 23, 42, 0.12)" : "1px solid transparent",
      fontWeight: active ? 800 : 600,
      boxShadow: active ? "0 1px 2px rgba(15, 23, 42, 0.10)" : "none",
      opacity: active ? 1 : 0.72,
      lineHeight: 1.1,
      whiteSpace: "nowrap",
    });

    const circleBtn = (highlighted) => ({
      width: 42,
      height: 42,
      borderRadius: 999,
      border: highlighted ? "2px solid #ff7a00" : "1px solid rgba(15, 23, 42, 0.20)",
      boxShadow: highlighted
        ? "0 0 0 4px rgba(255,122,0,0.22), 0 8px 16px rgba(15, 23, 42, 0.10)"
        : "0 6px 14px rgba(15, 23, 42, 0.08)",
      background: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      position: "relative",
      color: "#0f172a",
    });

    const highlightTag = {
      position: "absolute",
      top: -10,
      right: -10,
      background: "#ff7a00",
      color: "#ffffff",
      borderRadius: 999,
      padding: "2px 7px",
      fontSize: 10,
      fontWeight: 900,
      border: "1px solid rgba(0,0,0,0.08)",
      boxShadow: "0 2px 6px rgba(15, 23, 42, 0.14)",
    };

    const usernamePill = (active) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "4px 10px",
      borderRadius: 999,
      border: active ? "2px solid #ff7a00" : "1px solid rgba(15, 23, 42, 0.12)",
      background: active ? "rgba(255, 122, 0, 0.12)" : "rgba(255, 255, 255, 0.75)",
      boxShadow: active ? "0 0 0 4px rgba(255,122,0,0.18)" : "none",
      fontWeight: 800,
      color: "#0f172a",
    });

    return (
      <div style={shell} aria-hidden="true">
        <div style={seg}>
          <div style={segItem(false)}>Scan code</div>
          <div style={segItem(true)}>Venmo me</div>
          <div style={segItem(false)}>Show to pay</div>
        </div>

        <div className="mt-3 text-center">
          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>
            Tom Cat
          </div>
          <div className="mt-1" style={usernamePill(highlight === "username")}>
            <span style={{ opacity: 0.78, fontWeight: 900 }}>@</span>
            <span>Tom-Cat-11</span>
            {highlight === "username" ? <span style={highlightTag}>USERNAME</span> : null}
          </div>
        </div>

        <div
          className="mx-auto mt-3"
          style={{
            width: "min(260px, 100%)",
            height: 140,
            borderRadius: 14,
            border: "1px dashed rgba(15, 23, 42, 0.28)",
            background: "#f8fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            color: "rgba(15, 23, 42, 0.65)",
          }}
        >
          QR Code
        </div>

        <div className="mt-3" style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          <div style={circleBtn(false)}>⎙</div>
          <div style={circleBtn(false)}>✉</div>
          <div style={circleBtn(highlight === "copy")}>
            ⤴︎
            {highlight === "copy" ? <span style={highlightTag}>COPY</span> : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-3">
      <div className="as-optional-block">
        <div className="form-check form-switch as-optional-toggle">
          <input
            className="form-check-input"
            type="checkbox"
            id={toggleId}
            checked={!!enabled}
            onChange={(e) => handleEnableChange(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={toggleId}>
            <span className="as-field-label-badge" aria-hidden="true">💸</span>
            {t("participant.add_venmo_username_optional") || "Add Venmo username"}
            {isVenmoOk ? (
              <span
                className="ms-2 text-success fw-bold"
                aria-label={t("participant.venmo_ok") || "Looks good"}
                title={t("participant.venmo_ok") || "Looks good"}
              >
                ✅
              </span>
            ) : null}
          </label>
        </div>

        {enabled ? (
          <div className="mt-2">
            <div className="mt-2">
              <div className="nav nav-tabs as-venmo-tabs">
                <button
                  type="button"
                  className={`nav-link ${venmoTab === "username" ? "active" : ""}`}
                  onClick={() => {
                    setVenmoTab("username");
                    setVenmoError("");
                    setShowVenmoHelp(false);
                    setShowVenmoLinkHint(false);
                  }}
                >
                  <span className="as-venmo-tab-emoji" aria-hidden="true">🐾</span>
                  {t("participant.venmo_tab_username")}
                </button>
                <button
                  type="button"
                  className={`nav-link ${venmoTab === "link" ? "active" : ""}`}
                  onClick={() => {
                    setVenmoTab("link");
                    setVenmoError("");
                    setShowVenmoHelp(false);
                  }}
                >
                  <span className="as-venmo-tab-emoji" aria-hidden="true">🔗</span>
                  {t("participant.venmo_tab_link")}
                </button>
              </div>

              <div className="tab-content border border-top-0 rounded-bottom p-2">
                {venmoTab === "username" ? (
                  <div className="tab-pane active">
                    <input
                      type="text"
                      className={`form-control as-venmo-input ${venmoError ? "is-invalid" : ""}`}
                      value={venmoUsername}
                      onChange={(e) => handleVenmoUsernameChange(e.target.value)}
                      placeholder="Tom-Cat-11"
                      spellCheck={false}
                      autoCapitalize="off"
                      autoCorrect="off"
                      inputMode="text"
                      pattern={venmoUsernamePattern}
                      aria-invalid={venmoError ? "true" : "false"}
                    />
                    <div className="form-text">{t("participant.venmo_username_hint")}</div>
                    {venmoError ? <div className="invalid-feedback d-block">{venmoError}</div> : null}

                    <button
                      type="button"
                      className="btn btn-sm as-btn-help mt-2"
                      onClick={() => setShowVenmoHelp((v) => !v)}
                      aria-label={t("participant.venmo_find_username_toggle_aria")}
                      title={t("participant.venmo_find_username_toggle_aria")}
                    >
                      <span className="me-1" aria-hidden="true">🌿</span>
                      {showVenmoHelp
                        ? t("participant.venmo_find_link_toggle_hide")
                        : t("participant.venmo_find_username_toggle_show")}
                    </button>

                    {showVenmoHelp ? (
                      <>
                        <div className="alert alert-info mt-2 mb-0 p-2">
                          <VenmoHelpMock highlight="username" />
                          <div className="mt-2 small">{t("participant.venmo_username_help")}</div>
                        </div>
                        <div className="mt-2 d-md-none">
                          <button
                            type="button"
                            className="btn btn-sm as-btn-venmo w-100"
                            onClick={handleGoToVenmo}
                          >
                            {t("participant.go_to_venmo")}
                          </button>
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <div className="tab-pane active">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className={`form-control as-venmo-input ${venmoError ? "is-invalid" : ""}`}
                        value={venmoProfileLink}
                        onChange={(e) => handleVenmoLinkChange(e.target.value)}
                        placeholder="https://venmo.com/u/Tom-Cat-11"
                        spellCheck={false}
                        autoCapitalize="off"
                        autoCorrect="off"
                        inputMode="url"
                      />
                      <button
                        type="button"
                        className="btn btn-sm as-btn-soft"
                        onClick={() => setShowVenmoLinkHint((v) => !v)}
                        aria-label={t("participant.venmo_link_hint_toggle_aria")}
                        title={t("participant.venmo_link_hint_toggle_aria")}
                      >
                        ?
                      </button>
                    </div>

                    <div className="form-text mt-2">
                      {t("participant.venmo_link_parse_hint") ||
                        "Paste your Venmo profile link — we'll extract the username for you."}
                    </div>

                    {venmoProfileLink.trim() && venmoUsername.trim() && !venmoError ? (
                      <div className="form-text mt-1">
                        <strong>
                          {t("participant.venmo_link_parsed_username") || "Extracted username"}:
                        </strong>{" "}
                        <strong>{venmoUsername.trim().replace(/^@/, "")}</strong>
                      </div>
                    ) : null}

                    {showVenmoLinkHint ? (
                      <div className="form-text mt-2">{t("participant.venmo_profile_link_hint")}</div>
                    ) : null}

                    <button
                      type="button"
                      className="btn btn-sm as-btn-help mt-2"
                      onClick={() => setShowVenmoHelp((v) => !v)}
                      aria-label={t("participant.venmo_find_link_toggle_aria")}
                    >
                      <span className="me-1" aria-hidden="true">🌿</span>
                      {showVenmoHelp
                        ? t("participant.venmo_find_link_toggle_hide")
                        : t("participant.venmo_find_link_toggle_show")}
                    </button>

                    {showVenmoHelp ? (
                      <>
                        <div className="alert alert-info mt-2 mb-0 p-2">
                          <VenmoHelpMock highlight="copy" />
                          <div className="mt-2 small">{t("participant.venmo_help")}</div>
                        </div>
                        <div className="mt-2 d-md-none">
                          <button
                            type="button"
                            className="btn btn-sm as-btn-venmo w-100"
                            onClick={handleGoToVenmo}
                          >
                            {t("participant.go_to_venmo")}
                          </button>
                        </div>
                      </>
                    ) : null}

                    {venmoError ? (
                      <div className="invalid-feedback d-block">{venmoError}</div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

